import type { ReadonlySignal } from '@preact/signals-react';
import type { PoolPricingConfig } from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import type { PoolModel } from 'src/admin/models/PoolModel';

/**
 * Minimum trimmed length of the justification the delete-pool endpoint
 * accepts. Mirrors locates-ws's `MIN_DELETE_REASON_LENGTH`; enforced
 * client-side so the confirm button can stay disabled instead of
 * round-tripping a guaranteed 400.
 */
export const MIN_DELETE_POOL_REASON_LENGTH = 20;

/** Which of the two pool-management views is showing. */
export type PoolView = 'list' | 'detail';

export type PoolPermission = 'reader' | 'writer' | 'owner';

/**
 * Active = routing is on. Paused = routing is suspended; in-flight
 * orders complete, no new ones are sent. Derived from
 * `InventoryPool.active`: `'active'` when `true`, `'paused'` when
 * `false`. The richer UI enum gives room for additional statuses
 * (e.g. `'draining'`) without re-keying templates if the backend
 * widens the field later.
 */
export type PoolStatus = 'active' | 'paused';

/**
 * Status filter for the pool list. `'all'` disables the filter; every
 * other value matches a pool's `status` exactly. Derived from
 * {@link PoolStatus} so a new status is automatically a valid filter
 * value — the UI enumerates the selectable options separately.
 */
export type PoolStatusFilter = 'all' | PoolStatus;

/**
 * Model-owned action eligibility for one pool, from
 * {@link PoolListModel.poolActionEligibility}. The pool cards, list,
 * and Info tab all render from this, and the write commands enforce
 * the same policy, so visibility and authorization cannot diverge.
 */
export interface PoolActionEligibility {
    /**
     * The status is one the UI understands (`active` / `paused`). A
     * future status (e.g. `draining`) renders no pill and no toggle
     * rather than masquerading as Paused, until it grows a branch.
     */
    isKnownStatus: boolean;
    /** Pause/resume: any write permission on a pool in a known status. */
    canToggleStatus: boolean;
    /** Delete: owner access on a modern (non-legacy) pool. */
    canDelete: boolean;
}

export interface Pool {
    id: string;
    name: string;
    description: string | null;
    /** UUID of the firm that owns this pool. */
    firmId: string;
    /** Notification email for pool events (e.g. inventory-loaded summaries). */
    eventEmail: string;
    permission: PoolPermission;
    status: PoolStatus;
    /** The pool's persisted pricing config; `null` until an owner configures one. */
    pricing: PoolPricingConfig | null;
}

/**
 * How a newly created pool starts out: `'empty'` creates it with no
 * symbols; `'upload'` pre-opens the upload wizard on the new pool's
 * detail view right after creation.
 */
export type CreatePoolInitMode = 'empty' | 'upload';

/** Form values of the Create Pool dialog. */
export interface CreatePoolDraft {
    name: string;
    description: string;
    /** Notification email for pool events (backend `eventEmail`). */
    eventEmail: string;
    /**
     * Target firm (UUID) in the superuser scope; empty until picked.
     * Ignored in the firm-scoped view, where the page's firm wins.
     */
    firmId: string;
    initMode: CreatePoolInitMode;
}

export const initialCreatePoolDraft = {
    name: '',
    description: '',
    eventEmail: '',
    firmId: '',
    initMode: 'empty',
} satisfies CreatePoolDraft;

/**
 * The firm-scoped pool collection: the list query with its filters and
 * CSV export, per-pool action eligibility, the create / pause-resume /
 * delete flows (the pool cards invoke these for arbitrary pools, so
 * they live with the list), the firm-name lookups, and ownership of
 * the open pool's {@link PoolModel} — one per open detail view,
 * constructed by {@link openPoolDetail} and disposed on pool switch or
 * navigation, with the unsaved-changes discard guard in front of both.
 * Firm scope is part of the model's identity: {@link setFirm} is
 * one-shot and the owning route constructs a new instance per firm.
 */
export interface PoolListModel extends Disposable {
    // --- Loading / error ---
    readonly isLoading: ReadonlySignal<boolean>;
    readonly error: ReadonlySignal<string | null>;

    // --- Pool list ---
    readonly pools: ReadonlySignal<ReadonlyArray<Pool>>;
    readonly poolSearchQuery: ReadonlySignal<string>;
    readonly poolStatusFilter: ReadonlySignal<PoolStatusFilter>;
    /** Pools after applying both the search query and the status filter. */
    readonly filteredPools: ReadonlySignal<ReadonlyArray<Pool>>;
    /**
     * `firmId → firmName` lookup. Non-null only in the superuser scope
     * (firmId === null). The list view uses it to render per-card firm
     * names and to extend the search filter; the detail view uses it via
     * {@link selectedPoolFirmName}.
     */
    readonly firmNamesByFirmId: ReadonlySignal<Readonly<Record<string, string>> | null>;
    /**
     * Ids of legacy pools per the `legacy-lab-pools` feature flag, for
     * per-pool gates in list contexts (e.g. hiding a pool card's Delete
     * action, which the backend rejects for legacy pools).
     */
    readonly legacyPoolIds: ReadonlySignal<ReadonlySet<string>>;
    /**
     * Per-pool action eligibility keyed by pool id, covering every pool
     * in the current scope. The single owner of the pause/resume and
     * delete gates: the pool cards, the list, and the Info tab render
     * from it, and {@link requestTogglePoolStatus} /
     * {@link requestDeletePool} enforce the same policy.
     */
    readonly poolActionEligibility: ReadonlySignal<ReadonlyMap<string, PoolActionEligibility>>;

    // --- View / selection ---
    readonly view: ReadonlySignal<PoolView>;
    /** Pool id when the detail view is open, otherwise null. Available before the pool list query resolves. */
    readonly selectedPoolId: ReadonlySignal<string | null>;
    /**
     * Owning firm's display name for the currently-open pool, resolved
     * through {@link firmNamesByFirmId}. Null when no pool is open, when
     * the map isn't populated yet, or in the firm-scoped view (where the
     * firm is already in the page subtitle).
     */
    readonly selectedPoolFirmName: ReadonlySignal<string | null>;

    // --- Per-pool model (one per open pool) ---
    /**
     * The open pool's model, or `null` while the list view is showing.
     * One {@link PoolModel} instance exists per open pool and is
     * disposed on pool switch or navigation; it owns the pool's access
     * gates, detail tabs, info draft, permissions tab, and the
     * inventory / change-log / pricing children (the page-size
     * preferences alone are shared across instances by design).
     */
    readonly poolModel: ReadonlySignal<PoolModel | null>;
    /**
     * The pool model's unsaved-work aggregate; `false` while no pool is
     * open. Drives the discard prompts and the route blocker.
     */
    readonly hasUnsavedChanges: ReadonlySignal<boolean>;

    // --- Scope ---
    /**
     * Initialize the firm scope for the pool list. Pass `null` for
     * superuser "list all pools" mode; pass a firm id for the firm-scoped
     * view. The scope is part of the model's identity and is set exactly
     * once on a fresh instance — the owning route constructs a new model
     * when the firm changes. Calling this again with a different firm
     * throws.
     */
    setFirm(firmId: string | null): void;
    /**
     * Provide the `firmId → firmName` lookup for the superuser view, or
     * `null` to clear it (firm-scoped view doesn't need it). Driven from
     * the React shim's firms query.
     */
    setFirmNames(map: Readonly<Record<string, string>> | null): void;
    /**
     * Provide the scoped firm's display name in the firm-scoped view
     * (where {@link firmNamesByFirmId} is null), or `null` to clear it.
     * Used by the pool list CSV export's firm column.
     */
    setFirmName(name: string | null): void;

    /**
     * Wall-clock ms of the most recent point at which the open pool's
     * view became fresh: a manual {@link refresh} settled, or a
     * successful mutation (submit, upload, pool status toggle,
     * permissions change) finished invalidating its queries. Drives
     * the toolbar's "Last refresh" stamp so it auto-resets after every
     * pool change without the view tracking mutations directly.
     */
    readonly lastRefreshAt: ReadonlySignal<number>;

    // --- Navigation commands ---
    /**
     * Open the pool list. If the current detail view has unsaved
     * changes ({@link hasUnsavedChanges}), the navigation is staged and
     * {@link discardChangesOpen} flips to `true` so the view can prompt
     * the user. {@link confirmDiscardChanges} commits the stashed nav
     * after wiping edits; {@link cancelDiscardChanges} drops it.
     */
    openPoolList(): void;
    /**
     * Open the detail view for `poolId`. When the open pool already has
     * unsaved changes, behaves like {@link openPoolList}: the target
     * `poolId` is stashed behind the discard-confirmation gate so the
     * user has a chance to keep their work.
     */
    openPoolDetail(poolId: string): void;

    // --- Unsaved-changes guard ---
    /**
     * `true` while the model is holding a navigation request behind the
     * unsaved-changes confirm. The view renders the confirm dialog when
     * this is on. The pending action itself is private — the view only
     * needs to confirm or cancel; the model performs the deferred work.
     */
    readonly discardChangesOpen: ReadonlySignal<boolean>;
    /** Commit the stashed navigation: clears edits and runs the deferred command. */
    confirmDiscardChanges(): void;
    /**
     * Drop the stashed navigation; the user keeps editing the current
     * pool. Any `onCancel` registered via {@link requestDiscardChanges}
     * is fired so external blockers (e.g. the React Router blocker
     * watching for in-app link clicks) can reset themselves.
     */
    cancelDiscardChanges(): void;
    /**
     * External-navigation entry point: when there are no unsaved edits,
     * runs `proceed` immediately. Otherwise stashes both callbacks
     * behind the confirm dialog. The React shim's route blocker calls
     * this with `blocker.proceed` / `blocker.reset` so off-page link
     * clicks share the in-app confirm UX.
     */
    requestDiscardChanges(proceed: () => void, onCancel?: () => void): void;

    // --- Create pool ---
    /** True while the Create Pool dialog is open. */
    readonly createPoolOpen: ReadonlySignal<boolean>;
    /** The dialog's form values. Reset every time the dialog opens. */
    readonly createPoolDraft: ReadonlySignal<CreatePoolDraft>;
    /**
     * `true` in the superuser "all firms" scope, where the draft must
     * name a target firm (the dialog renders a firm selector). `false`
     * in the firm-scoped view, where the page's firm is the target.
     */
    readonly createPoolNeedsFirm: ReadonlySignal<boolean>;
    /**
     * Whether the draft is submittable: name present, event email
     * shaped like an email, a firm picked when {@link createPoolNeedsFirm},
     * and no POST already in flight.
     */
    readonly canSubmitCreatePool: ReadonlySignal<boolean>;
    /** True while the create-pool POST is in flight. */
    readonly isCreatingPool: ReadonlySignal<boolean>;
    /** Last create-pool failure message, or null. Rendered inside the dialog. */
    readonly createPoolError: ReadonlySignal<string | null>;
    /** Open the Create Pool dialog with a fresh draft and clean submit slate. */
    openCreatePool(): void;
    /** Close the Create Pool dialog, dropping any lingering error. */
    closeCreatePool(): void;
    /** Merge form-field edits into {@link createPoolDraft}. */
    updateCreatePoolDraft(patch: Partial<CreatePoolDraft>): void;
    /**
     * POST the current draft. No-op (returns `null`) unless
     * {@link canSubmitCreatePool}. In the firm-scoped view the page's
     * firm is the target and the draft's `firmId` is ignored. On
     * success the dialog closes, a toast fires, the pool list is
     * invalidated, the upload wizard is pre-opened when the draft's
     * `initMode` is `'upload'`, and the new pool's id is returned —
     * the view is responsible for pushing the detail URL (the URL,
     * not the model, is the source of truth for the open pool; see
     * `PoolManagement.tsx`). On failure the dialog stays open with
     * {@link createPoolError} set and `null` is returned.
     */
    submitCreatePool(): Promise<string | null>;

    // --- Delete pool ---
    /**
     * Pool whose delete-confirm dialog is open, or `null`. Resolved
     * from the pool list at read time so the dialog always renders the
     * current name.
     */
    readonly pendingDeletePool: ReadonlySignal<Pool | null>;
    /** The reason the user has typed into the delete-confirm dialog. */
    readonly deletePoolReason: ReadonlySignal<string>;
    /**
     * Whether the delete is submittable: a pending pool, a trimmed
     * reason of at least {@link MIN_DELETE_POOL_REASON_LENGTH}
     * characters, and no DELETE already in flight.
     */
    readonly canConfirmDeletePool: ReadonlySignal<boolean>;
    /** True while the delete-pool DELETE is in flight. */
    readonly isDeletingPool: ReadonlySignal<boolean>;
    /** Last delete-pool failure message, or null. Rendered inside the dialog. */
    readonly deletePoolError: ReadonlySignal<string | null>;
    /**
     * Open the delete-confirm dialog for `poolId` with a blank reason.
     * No-op unless the caller holds `owner` on the pool — deletion
     * additionally requires owner access on the pool's firm, which the
     * pool payload can't distinguish; the backend rejects the rest.
     * Also a no-op for legacy pools ({@link legacyPoolIds}), which the
     * backend refuses to delete.
     */
    requestDeletePool(poolId: string): void;
    /** Close the delete-confirm dialog. Ignored while the DELETE is in flight. */
    cancelDeletePool(): void;
    /** Replace the staged deletion reason. */
    setDeletePoolReason(reason: string): void;
    /**
     * Issue the DELETE for the pending pool. No-op (returns `false`)
     * unless {@link canConfirmDeletePool}. On success the dialog
     * closes, a toast fires, the pool list is invalidated (which drops
     * the card from the list view), and `true` is returned. On failure
     * the dialog stays open with {@link deletePoolError} set and
     * `false` is returned.
     */
    confirmDeletePool(): Promise<boolean>;

    // --- Pool status ---
    /** Id of the pool whose pause-confirm modal is open, or `null`. */
    readonly pendingPauseId: ReadonlySignal<string | null>;
    /**
     * Route a status toggle to pause-with-confirmation (active pool) or
     * instant resume (paused pool). An unknown future status no-ops
     * until the UI grows an explicit branch — the transition decision is
     * owned here, not by the views.
     */
    requestTogglePoolStatus(poolId: string): void;
    /** Open the pause-confirm modal for `poolId`. No-op on a paused pool or reader scope. */
    requestPausePool(poolId: string): void;
    /** Close the pause-confirm modal without changing status. */
    cancelPausePool(): void;
    /** Commit the pending pause. Safe to call when no pool is pending. */
    confirmPausePool(): void;
    /** Flip a paused pool back to active. No confirmation prompt. */
    resumePool(poolId: string): void;

    // --- Search commands ---
    setPoolSearchQuery(query: string): void;
    setPoolStatusFilter(filter: PoolStatusFilter): void;

    /**
     * Trigger a CSV download of the pool list as currently filtered
     * (search query + status filter). No-op when the filtered list is
     * empty or when running outside a browser.
     */
    downloadPoolListCsv(): void;

    refresh(): Promise<void>;
}
